import React, { useCallback, useEffect, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import type { BlockerFunction } from "react-router-dom";
import { DesignDialog, DesignDialogButton } from "./dashboard/DesignDialog";
import { announceSavedChanges, DEFAULT_SAVED_CHANGES_MESSAGE } from "./savedChangesNotice";
import {
  getMissingFieldsPendingLeave,
  isMissingFieldsDialogOpen,
  setMissingFieldsPendingLeave,
} from "./dashboard/missingFieldsLeaveStore";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../utils/userFacingError";

// Time for the page's REQUIRED FIELDS MISSING pop-up to open after its save
// stops. Without that pop-up the user simply stays on the page.
const missingFieldsDialogWaitMs = 400;

// Claude Design (16) CMP Dashboard (CLAUDE.md "unsaved changes" rule,
// navLeaveSave): every UNSAVED CHANGES pop-up has a red circle with a white
// warning triangle, one bold line, a gold "Save & Update" and a dark "Leave
// Without Saving". "Save & Update" runs the page's own save. When it saves,
// the user goes on to the page they chose and sees the saved confirmation.
// When the save stops on validation, the pop-up closes and the page shows
// the errors. Pages pass the same save as their own Save / Save & Update
// button, with the current form values.
export type UnsavedChangesVariant = "page" | "save";
export const UNSAVED_CHANGES_MESSAGE =
  "Click Save & Update before leaving or changes will be lost.";
/** @deprecated Design (16) uses UNSAVED_CHANGES_MESSAGE on every page. */
export const PAGE_UNSAVED_CHANGES_MESSAGE = UNSAVED_CHANGES_MESSAGE;

/** A save run from the pop-up: true when saved, false when it stopped. */
export type UnsavedChangesSave = () => Promise<boolean>;

type UnsavedChangesPromptProps = {
  /** @deprecated The design text is the same on every page. */
  message?: string;
  /** @deprecated Every pop-up uses the design (16) layout. */
  variant?: UnsavedChangesVariant;
  onSave?: UnsavedChangesSave;
  /** The saved confirmation shown on the next page. */
  savedMessage?: string | (() => string);
  savedHighlights?: readonly string[] | (() => readonly string[]);
  saving?: boolean;
  saveError?: string;
  when: boolean;
};

// Route links keep the established capture-phase warning. Back and Forward
// use React Router's data-router blocker, which does not add or remove browser
// history entries. The blocker is intentionally limited to POP navigation so
// existing save-success redirects can continue after their page clears its
// saved-state baseline.

type PendingNavigation = { kind: "href"; href: string } | { kind: "history" };

type UnsavedChangesDialogProps = {
  /** @deprecated The design text is the same on every pop-up. */
  message?: string;
  /** @deprecated Every pop-up uses the design (16) layout. */
  variant?: UnsavedChangesVariant;
  onLeave: () => void;
  /** Runs the page save; without it "Save & Update" closes the pop-up. */
  onSave?: () => void;
  onStay: () => void;
  saving?: boolean;
  saveError?: string;
};

export const UnsavedChangesDialog = ({
  onLeave,
  onSave,
  onStay,
  saving = false,
  saveError,
}: UnsavedChangesDialogProps) => (
  <DesignDialog
    open
    onClose={onStay}
    title="UNSAVED CHANGES"
    message={<strong className="font-bold">{UNSAVED_CHANGES_MESSAGE}</strong>}
    icon="warningTriangle"
    busy={saving}
    variant="unsaved"
    closeLabel="Close unsaved changes warning"
    actions={
      <>
        <DesignDialogButton
          autoFocus
          disabled={saving}
          className="max-w-[210px] whitespace-nowrap"
          onClick={onSave || onStay}
        >
          {saving ? "Saving..." : "Save & Update"}
        </DesignDialogButton>
        <DesignDialogButton tone="dark" disabled={saving} className="max-w-[210px] whitespace-nowrap" onClick={onLeave}>
          Leave Without Saving
        </DesignDialogButton>
      </>
    }
  >
    {saveError && <p role="alert" className="mt-3 text-sm text-[#E24C4C]">{saveError}</p>}
  </DesignDialog>
);

const UnsavedChangesPrompt = ({
  onSave,
  savedMessage,
  savedHighlights,
  saving = false,
  saveError,
  when,
}: UnsavedChangesPromptProps) => {
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [unconfirmedSaveError, setUnconfirmedSaveError] = useState("");
  const navigate = useNavigate();
  const blockHistoryNavigation = useCallback<BlockerFunction>(
    ({ historyAction }) => when && historyAction === "POP",
    [when],
  );
  const blocker = useBlocker(blockHistoryNavigation);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);

  useEffect(() => {
    if (!when) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;

      if (
        !anchor ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      const destination = new URL(anchor.href, window.location.href);
      const current = new URL(window.location.href);
      const isSameDocumentHash =
        destination.origin === current.origin &&
        destination.pathname === current.pathname &&
        destination.search === current.search &&
        destination.hash &&
        destination.hash !== current.hash;

      if (destination.origin !== current.origin || isSameDocumentHash) {
        return;
      }

      if (destination.href === current.href) {
        return;
      }

      event.preventDefault();
      setPendingNavigation({ kind: "href", href: destination.href });
    };

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [when]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setPendingNavigation({ kind: "history" });
    }
  }, [blocker.state]);

  useEffect(() => {
    if (when || saveInProgress) {
      return;
    }

    setPendingNavigation(null);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker, saveInProgress, when]);

  if (!pendingNavigation) {
    return null;
  }

  const handleLeave = (navigation: PendingNavigation = pendingNavigation) => {
    if (navigation.kind === "history") {
      setPendingNavigation(null);
      if (blocker.state === "blocked") {
        blocker.proceed();
      }
      return;
    }

    const href = navigation.href;
    const destination = new URL(href, window.location.href);

    setPendingNavigation(null);

    if (destination.origin === window.location.origin) {
      navigate(
        `${destination.pathname}${destination.search}${destination.hash}`,
      );
      return;
    }

    window.location.assign(href);
  };

  const handleStay = (navigation: PendingNavigation = pendingNavigation) => {
    if (navigation.kind === "history" && blocker.state === "blocked") {
      blocker.reset();
    }
    setPendingNavigation(null);
  };

  // Validation stopped the save. The page shows its errors; its REQUIRED
  // FIELDS MISSING pop-up can still go on to the chosen page with "Leave
  // Without Saving" (Update Member Form.dc.html errPop fromLeave).
  const keepNavigationForMissingFields = () => {
    const navigation = pendingNavigation;
    const pendingLeave = {
      leave: () => handleLeave(navigation),
      stay: () => handleStay(navigation),
    };
    setPendingNavigation(null);
    setMissingFieldsPendingLeave(pendingLeave);
    window.setTimeout(() => {
      if (getMissingFieldsPendingLeave() !== pendingLeave || isMissingFieldsDialogOpen()) return;
      setMissingFieldsPendingLeave(null);
      handleStay(navigation);
    }, missingFieldsDialogWaitMs);
  };

  const handleSave = async () => {
    if (!onSave || saveInProgress || saving) {
      return;
    }

    setSaveInProgress(true);
    setUnconfirmedSaveError("");

    try {
      const saveConfirmed = await onSave();

      if (saveConfirmed) {
        const message = typeof savedMessage === "function" ? savedMessage() : savedMessage;
        const highlights = typeof savedHighlights === "function" ? savedHighlights() : savedHighlights;
        handleLeave();
        announceSavedChanges({ message: message || DEFAULT_SAVED_CHANGES_MESSAGE, highlights });
      } else {
        // Validation stopped the save: stay on the page and show its errors.
        keepNavigationForMissingFields();
      }
    } catch (error) {
      const reason = getUserFacingErrorText(error, "");
      setUnconfirmedSaveError(
        reason
          ? `${reason} Your changes are still on this page.`
          : buildStaffErrorFallback("save your changes", "Your changes are still on this page."),
      );
    } finally {
      setSaveInProgress(false);
    }
  };

  return (
    <UnsavedChangesDialog
      onLeave={() => handleLeave()}
      onSave={onSave ? () => void handleSave() : undefined}
      onStay={() => handleStay()}
      saving={saving || saveInProgress}
      saveError={saveError || unconfirmedSaveError}
    />
  );
};

export default UnsavedChangesPrompt;
